/*
 * Pakkeknappen: laegger tre varer og en gave i kurven med ét kald.
 *
 * Hvorfor ét kald og ikke fire: /cart/add tager et items-array, og et enkelt
 * kald er atomart. Fire kald efter hinanden kan lande halvt, og saa staar
 * kunden med to af tre varer og ingen gave.
 *
 * Hvorfor vi sender kunden til /cart bagefter i stedet for at aabne temaets
 * cart-notification: notifikationen bygger sin vaelger af parsedState.id
 * (`#cart-notification-product-${id}` i cart-notification.js), og det felt
 * findes kun naar der er lagt ÉN vare i kurven. Med et items-array er det
 * undefined, og renderContents ville kaste. Kurven er desuden det rigtige sted
 * at lande: dér ser kunden alle fire linjer, ser at gaven staar til 0 kr, og
 * har MyCosmo-widgetten lige foran sig.
 *
 * Linjeegenskaben "Pakke" saettes paa alle fire linjer. Den foelger med til
 * ordren, saa pakkeren kan se at de hoerer sammen, og saa omsaetningen kan
 * opgoeres pr. pakke i BigQuery.
 */
if (!customElements.get('pakke-knap')) {
  customElements.define(
    'pakke-knap',
    class PakkeKnap extends HTMLElement {
      connectedCallback() {
        if (this.bundet) return;
        this.bundet = true;

        this.knap = this.querySelector('button');
        this.spinner = this.querySelector('.pakke__spinner');
        this.fejl = this.querySelector('.pakke__fejl');
        if (this.knap) this.knap.addEventListener('click', this.laegIKurv.bind(this));
      }

      get linjer() {
        const navn = this.dataset.navn || '';
        const varer = (this.dataset.ids || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
          .map((id) => ({
            id: Number(id),
            quantity: 1,
            properties: { Pakke: navn },
          }));

        const gave = Number(this.dataset.gave);
        if (gave) {
          varer.push({
            id: gave,
            quantity: 1,
            properties: { Pakke: navn, Gave: 'Ja' },
          });
        }
        return varer;
      }

      laegIKurv() {
        const linjer = this.linjer;
        if (linjer.length < 2) return;

        this.visIndlaeser(true);
        this.visFejl('');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({ items: linjer });

        fetch(`${routes.cart_add_url}`, config)
          .then((r) => r.json())
          .then((svar) => {
            // Shopify svarer med et status-felt naar noget gik galt, fx hvis en
            // vare blev udsolgt mellem sidevisningen og klikket.
            if (svar.status) {
              this.visFejl(svar.description || 'Pakken kunne ikke laegges i kurven.');
              this.visIndlaeser(false);
              return;
            }
            this.spor(linjer);
            window.location.href = routes.cart_url || '/cart';
          })
          .catch((e) => {
            console.error(e);
            this.visFejl('Der skete en fejl. Proev igen.');
            this.visIndlaeser(false);
          });
      }

      /*
       * Eget event ved siden af temaets add_to_cart, som posthog-tracking.liquid
       * opsnapper paa netvaerkslaget. Uden det kan de seks pakker ikke skilles
       * ad i PostHog, fordi de alle rammer den samme /cart/add.
       */
      spor(linjer) {
        if (typeof window.posthog === 'undefined' || !window.posthog.capture) return;
        try {
          window.posthog.capture('pakke_lagt_i_kurv', {
            pakke: this.dataset.navn || '',
            antal_varer: linjer.length,
            variant_ids: linjer.map((l) => l.id),
          });
        } catch (e) {
          /* Sporing maa aldrig staa i vejen for et koeb. */
        }
      }

      visIndlaeser(i_gang) {
        if (this.knap) {
          this.knap.disabled = i_gang;
          this.knap.setAttribute('aria-busy', i_gang ? 'true' : 'false');
        }
        if (this.spinner) this.spinner.hidden = !i_gang;
      }

      visFejl(besked) {
        if (!this.fejl) return;
        this.fejl.textContent = besked;
        this.fejl.hidden = !besked;
      }
    }
  );
}

/*
 * Kopiér-link-knappen i indholdsfortegnelsen.
 *
 * URL'en (canonical_url + #anker) er allerede den fulde, rigtige adresse,
 * bygget i Liquid. JS'en gør kun selve kopieringen og giver et kvitteringstegn,
 * så en kollega kan se at klikket virkede uden at skulle tjekke sin
 * udklipsholder.
 *
 * navigator.clipboard kræver https, hvilket cosmolaser.dk altid er, men fejler
 * stille i browsere uden support (fx meget gamle). Falder tilbage til at
 * markere teksten i en skjult textarea og bruge execCommand, som stadig virker
 * de fleste steder selv om det er en ældre API.
 */
if (!customElements.get('kopier-link')) {
  customElements.define(
    'kopier-link',
    class KopierLink extends HTMLElement {
      connectedCallback() {
        if (this.bundet) return;
        this.bundet = true;

        this.knap = this.querySelector('button');
        this.tekstElement = this.querySelector('.pakker__toc-kopier-tekst');
        this.grundtekst = this.tekstElement ? this.tekstElement.textContent : '';

        if (this.knap) this.knap.addEventListener('click', this.kopier.bind(this));
      }

      kopier() {
        const url = this.dataset.url;
        if (!url) return;

        const efterKopiering = () => {
          if (!this.tekstElement) return;
          this.tekstElement.textContent = 'Kopieret!';
          clearTimeout(this.nulstilTimer);
          this.nulstilTimer = setTimeout(() => {
            this.tekstElement.textContent = this.grundtekst;
          }, 2000);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(efterKopiering).catch(() => this.faldTilbage(url, efterKopiering));
        } else {
          this.faldTilbage(url, efterKopiering);
        }
      }

      faldTilbage(url, saa) {
        const felt = document.createElement('textarea');
        felt.value = url;
        felt.setAttribute('readonly', '');
        felt.style.position = 'absolute';
        felt.style.left = '-9999px';
        document.body.appendChild(felt);
        felt.select();
        try {
          document.execCommand('copy');
          saa();
        } catch (e) {
          console.error('kopier-link:', e);
        }
        document.body.removeChild(felt);
      }
    }
  );
}
